/**
 * @file InterruptedDraftBanner.tsx
 * @brief Continue / Process / Discard for durable web boardroom drafts.
 *
 * @author DES
 * @date 28-07-2026
 */

import { releaseBoardroomHold } from "./billingPreauth";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoaderCircle, Play, Trash2, TriangleAlert, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/shared/auth/auth-context";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { showBillingErrorToast } from "@/features/my-billing/billingToast";
import { toast } from "@/shared/hooks/useToast";
import { preauthorizeBoardroom } from "./billingPreauth";
import {
  assembleDraftBlob,
  deleteDraft,
  listDraftsForUser,
  patchDraftMeta,
  type BoardroomDraftMeta,
} from "./capture/boardroomDraftStore";
import { uploadAndCreateBoardroomMeeting } from "./captureApi";
import { useBoardroomBasePath } from "./useBoardroomBasePath";
import { apiPost } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";

/** The notes written during this draft's meeting (BoardroomRecorderPage
 *  keeps them under `vz.meetingNotes.<draftId>`). */
function draftMeetingNoteIds(draftId: string): string[] {
  try {
    const raw = window.localStorage.getItem(`vz.meetingNotes.${draftId}`);
    const parsed = raw ? (JSON.parse(raw) as { ids?: unknown }) : null;
    return Array.isArray(parsed?.ids)
      ? parsed.ids.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}
function forgetDraftMeetingNotes(draftId: string) {
  try {
    window.localStorage.removeItem(`vz.meetingNotes.${draftId}`);
  } catch {
    /* ignore */
  }
}

/**
 * `tier` picks which drafts this screen offers (DES 07-10-2026): Boardroom
 * drafts on the meetings pages, Meeting Notes drafts in the Notes Library.
 * A discarded Notes recording used to surface on Boardrooms.
 */
export function InterruptedDraftBanner({ tier = "full" }: { tier?: "full" | "notes" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = useBoardroomBasePath();
  const [drafts, setDrafts] = useState<BoardroomDraftMeta[]>([]);
  const [busy, setBusy] = useState<"process" | "discard" | null>(null);

  const reload = useCallback(async () => {
    if (!user?.id) {
      setDrafts([]);
      return;
    }
    const all = await listDraftsForUser(user.id);
    setDrafts(all.filter((d) => (d.tier === "notes") === (tier === "notes")));
  }, [user?.id, tier]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const draft = drafts[0];
  if (!draft) return null;

  const onContinue = () => {
    navigate(`${base}/meetings/record`, {
      state: {
        resumeDraftId: draft.draftId,
        title: draft.title,
        targetLanguages: draft.targetLanguages,
        participants: draft.participants,
        deviceId: draft.deviceId,
        // Back into the same mode (Meeting Notes or full).
        tier: draft.tier === "notes" ? "notes" : "full",
      },
    });
  };

  const onProcess = async () => {
    setBusy("process");
    try {
      const assembled = await assembleDraftBlob(draft.draftId);
      if (!assembled) {
        toast({
          title: "Nothing to process",
          description: "No audio was saved in this draft.",
          variant: "destructive",
        });
        return;
      }

      let meta = assembled.meta;
      // Older drafts / interrupted before preauth landed — place hold now.
      if (!meta.paymentIntentId) {
        const notesDraft = meta.tier === "notes";
        const preauth = await preauthorizeBoardroom(notesDraft ? "notes" : "full");
        // Free plan (B4, 22-10-2026): a Notes draft started free has no hold.
        // Minutes used up since then must not lose what was recorded (a
        // running recording is never cut), so it is processed free. Same for
        // a plan's Notes past fair use since (B6 FAIR_USE_REACHED).
        // B43 (31-10-2026): Free Full Intelligence drafts too (Free is never
        // charged; the server keeps it as Notes if no Full minutes are left).
        const freeDone =
          !preauth.ok &&
          (((preauth.error === "FREE_MINUTES_USED" || preauth.error === "FREE_LIMIT_REACHED") &&
            preauth.details?.reason !== "device_used" &&
            preauth.details?.reason !== "email_not_allowed") ||
            preauth.error === "FREE_INTEL_LIMIT" ||
            (notesDraft && preauth.error === "FAIR_USE_REACHED"));
        if (!preauth.ok && !freeDone) {
          const shown = showBillingErrorToast({
            code: preauth.error,
            audience: billingAudienceFor(user),
            serverMessage: preauth.message,
              details: preauth.details,
            navigate: (to) => navigate(to),
          });
          if (!shown) {
            toast({
              variant: "destructive",
              title: "Billing authorization failed",
              description: preauth.message ?? "Authorize billing before processing this draft.",
            });
          }
          return;
        }
        if (preauth.ok) {
          const patched = await patchDraftMeta(draft.draftId, {
            paymentIntentId: preauth.paymentIntentId ?? null,
            authorizedCents: preauth.authorizedCents ?? null,
          });
          if (patched) meta = patched;
          else {
            meta = {
              ...meta,
              paymentIntentId: preauth.paymentIntentId ?? null,
              authorizedCents: preauth.authorizedCents ?? null,
            };
          }
        }
      }

      const meeting = await uploadAndCreateBoardroomMeeting({
        blob: assembled.blob,
        meta,
      });
      // The notes written during the meeting, linked privately as the
      // recorder does after a normal end (DES 07-10-2026: a rescued meeting
      // left them unlinked in Personal notes). Fire-and-forget.
      for (const noteId of draftMeetingNoteIds(draft.draftId)) {
        void apiPost(boardroomEndpoints.prepNotes(meeting.id), {
          noteId,
          role: "addendum",
        }).catch((err) => console.warn("[Interrupted] Meeting note link failed:", noteId, err));
      }
      forgetDraftMeetingNotes(draft.draftId);
      await deleteDraft(draft.draftId);
      toast({
        title: "Meeting uploaded",
        // A recovered Meeting Notes draft keeps its tier (ADR-030, 06-10-2026).
        description:
          meta.tier === "notes"
            ? "Your meeting notes are being written."
            : "Diarization has started.",
      });
      navigate(`${base}/meetings/${meeting.id}`);
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
      void reload();
    }
  };

  const discardOne = async (d: BoardroomDraftMeta) => {
    await deleteDraft(d.draftId);
    // The typed notes themselves stay in Personal notes; only the list goes.
    forgetDraftMeetingNotes(d.draftId);
    // Its card hold goes back too (02-10-2026).
    void releaseBoardroomHold(d.paymentIntentId);
  };

  // Several interrupted recordings stack up (a refresh mid-test leaves one
  // each). The banner shows one at a time, so discarding the first showed the
  // next, identical-looking one and read as "Discard does nothing" (DES
  // 07-10-2026). It now says how many there are and offers Discard all.
  const others = drafts.length - 1;
  const onDiscard = async (all: boolean) => {
    const targets = all ? drafts : [draft];
    const msg = all
      ? `Discard all ${targets.length} interrupted recordings permanently?`
      : "Discard this interrupted recording permanently?";
    if (!window.confirm(msg)) return;
    setBusy("discard");
    try {
      for (const d of targets) await discardOne(d);
      const left = drafts.length - targets.length;
      toast({
        title: all || left === 0 ? "Discarded" : "Draft discarded",
        description: left > 0 ? `${left} more interrupted recording${left === 1 ? "" : "s"} below.` : undefined,
      });
      await reload();
    } finally {
      setBusy(null);
    }
  };

  const mins = Math.max(1, Math.round(draft.elapsedMs / 60000));
  // A Meeting Notes draft says so (DES 07-10-2026: a discarded Notes
  // recording came back as "Interrupted boardroom"). Older Notes drafts were
  // saved with the boardroom fallback title, so that one reads as theirs too.
  const isNotes = draft.tier === "notes";
  const draftTitle =
    isNotes && (!draft.title || draft.title === "Boardroom Session")
      ? "Meeting notes"
      : draft.title || "Untitled";

  return (
    <div className="border-b border-r border-border/60 bg-warning-soft/40 px-4 py-4 md:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <p className="text-sm font-semibold">
              {isNotes ? "Interrupted meeting notes" : "Interrupted boardroom"}
              {others > 0 ? (
                <span className="ml-2 font-normal text-muted-foreground">1 of {drafts.length}</span>
              ) : null}
            </p>
            <p className="text-sm text-muted-foreground">
              “{draftTitle}” (~{mins} min) was saved locally after a refresh or close. Continue
              recording, process what you have, or discard.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onContinue}>
            <Play className="mr-1.5 h-3.5 w-3.5" />
            Continue
          </Button>
          <Button type="button" size="sm" disabled={busy !== null} onClick={() => void onProcess()}>
            {busy === "process" ? (
              <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-3.5 w-3.5" />
            )}
            Process
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy !== null}
            onClick={() => void onDiscard(false)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            Discard
          </Button>
          {others > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy !== null}
              onClick={() => void onDiscard(true)}
            >
              Discard all ({drafts.length})
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
